import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, AreaChart, Area,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import { Lock, Eye, Users, MousePointerClick, Mail, LogOut, RefreshCw, TrendingUp, Smartphone, Monitor } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "scr_admin_token";

const COLORS = ["#0E2A56", "#D8232A", "#2762A8", "#E3C16F", "#1D8B9B"];

const formatDay = (d) => {
  try {
    return new Date(d + "T00:00:00Z").toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return d;
  }
};

const Kpi = ({ icon: Icon, label, value, sub }) => (
  <div className="rounded-2xl bg-white border border-brand-navy/10 p-5">
    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-brand-navy/60 mb-2">
      <Icon size={14} className="text-brand-red" />
      {label}
    </div>
    <div className="font-heading font-black text-3xl text-brand-navy tracking-tight">{value}</div>
    {sub && <div className="text-xs text-brand-navy/60 mt-1 font-medium">{sub}</div>}
  </div>
);

const AdminAnalytics = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [input, setInput] = useState("");
  const [data, setData] = useState(null);
  const [leads, setLeads] = useState([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async (currentToken, currentDays) => {
    if (!currentToken) return;
    setLoading(true);
    setError("");
    try {
      const [summary, recentLeads] = await Promise.all([
        axios.get(`${API}/admin/analytics/summary`, {
          params: { days: currentDays },
          headers: { "x-admin-token": currentToken },
        }),
        axios.get(`${API}/admin/leads/recent`, {
          params: { limit: 15 },
          headers: { "x-admin-token": currentToken },
        }),
      ]);
      setData(summary.data);
      setLeads(recentLeads.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        setError("Invalid admin token.");
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else {
        setError("Failed to load analytics. Try again.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = "Analytics · Ship Costa Rica Admin";
    if (token) load(token, days);
  }, [token, days, load]);

  const submitLogin = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    localStorage.setItem(TOKEN_KEY, input.trim());
    setToken(input.trim());
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setData(null);
    setLeads([]);
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-cream p-5">
        <form
          onSubmit={submitLogin}
          data-testid="admin-login-form"
          className="w-full max-w-md rounded-3xl bg-white border border-brand-navy/10 p-8 shadow-[0_25px_50px_-20px_rgba(14,42,86,0.25)]"
        >
          <div className="w-12 h-12 rounded-2xl bg-brand-navy text-brand-cream flex items-center justify-center mb-5">
            <Lock size={22} />
          </div>
          <h1 className="font-heading font-black text-2xl text-brand-navy">Admin Analytics</h1>
          <p className="text-sm text-brand-navy/65 mt-1">Enter your admin token to view site analytics.</p>
          <input
            type="password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Admin token"
            data-testid="admin-token-input"
            className="mt-6 w-full rounded-full bg-brand-cream border border-brand-navy/15 px-5 py-3 text-brand-navy font-medium focus:outline-none focus:ring-2 focus:ring-brand-red/40"
            autoFocus
          />
          {error && <div className="mt-3 text-sm text-brand-red font-semibold">{error}</div>}
          <button
            type="submit"
            data-testid="admin-login-submit"
            className="mt-5 w-full rounded-full bg-brand-red hover:bg-brand-navy text-white font-heading font-bold py-3 transition-colors"
          >
            Sign in
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-cream p-5 sm:p-8 lg:p-12">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <div className="text-xs font-black uppercase tracking-[0.22em] text-brand-red mb-1">Ship Costa Rica</div>
            <h1 className="font-heading font-black text-3xl sm:text-4xl text-brand-navy tracking-tight">Analytics Dashboard</h1>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center rounded-full bg-white border border-brand-navy/15 p-1">
              {[7, 30, 90].map((d) => (
                <button
                  key={d}
                  onClick={() => setDays(d)}
                  data-testid={`range-${d}d`}
                  className={`px-4 py-1.5 rounded-full text-xs font-bold transition ${
                    days === d ? "bg-brand-navy text-brand-cream" : "text-brand-navy/65 hover:text-brand-navy"
                  }`}
                >
                  {d}d
                </button>
              ))}
            </div>
            <button
              onClick={() => load(token, days)}
              className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white border border-brand-navy/15 text-brand-navy hover:bg-brand-navy hover:text-brand-cream transition"
              aria-label="Refresh"
              data-testid="refresh-btn"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full bg-white border border-brand-navy/15 px-4 py-2 text-xs font-bold text-brand-navy/75 hover:bg-brand-navy hover:text-brand-cream transition"
              data-testid="logout-btn"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-2xl bg-brand-red/10 border border-brand-red/20 text-brand-red font-semibold p-4 mb-6">
            {error}
          </div>
        )}

        {!data ? (
          <div className="text-brand-navy/60 font-medium">Loading…</div>
        ) : (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Kpi icon={Eye} label="Views today" value={data.kpi.views_today} sub={`Sessions: ${data.kpi.sessions_today}`} />
              <Kpi icon={TrendingUp} label="Views this week" value={data.kpi.views_week} sub={`Sessions: ${data.kpi.sessions_week}`} />
              <Kpi icon={Users} label="Views this month" value={data.kpi.views_month} sub={`Sessions: ${data.kpi.sessions_month}`} />
              <Kpi icon={Mail} label="Leads (total)" value={data.kpi.leads_total} sub={`This week: ${data.kpi.leads_week}`} />
            </div>

            {/* Daily views chart */}
            <div className="rounded-3xl bg-white border border-brand-navy/10 p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading font-black text-xl text-brand-navy">Daily views & sessions · last {data.range_days} days</h2>
              </div>
              <div style={{ width: "100%", height: 320 }}>
                <ResponsiveContainer>
                  <AreaChart data={data.daily} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="vGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#D8232A" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#D8232A" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="sGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0E2A56" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#0E2A56" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#0E2A5615" />
                    <XAxis dataKey="date" tickFormatter={formatDay} tick={{ fontSize: 11, fill: "#0E2A56" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#0E2A56" }} />
                    <Tooltip
                      labelFormatter={formatDay}
                      contentStyle={{ borderRadius: 12, border: "1px solid #0E2A5620" }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Area type="monotone" dataKey="views" stroke="#D8232A" strokeWidth={2.5} fill="url(#vGrad)" />
                    <Area type="monotone" dataKey="sessions" stroke="#0E2A56" strokeWidth={2.5} fill="url(#sGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Events by type */}
              <div className="rounded-3xl bg-white border border-brand-navy/10 p-6">
                <h2 className="font-heading font-black text-lg text-brand-navy mb-4 flex items-center gap-2">
                  <MousePointerClick size={18} className="text-brand-red" /> Events
                </h2>
                <div style={{ width: "100%", height: 280 }}>
                  <ResponsiveContainer>
                    <BarChart data={data.events_by_type} layout="vertical" margin={{ left: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#0E2A5615" />
                      <XAxis type="number" tick={{ fontSize: 11, fill: "#0E2A56" }} />
                      <YAxis type="category" dataKey="event_type" tick={{ fontSize: 11, fill: "#0E2A56" }} width={140} />
                      <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #0E2A5620" }} />
                      <Bar dataKey="count" fill="#0E2A56" radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Device split */}
              <div className="rounded-3xl bg-white border border-brand-navy/10 p-6">
                <h2 className="font-heading font-black text-lg text-brand-navy mb-4 flex items-center gap-2">
                  <Smartphone size={18} className="text-brand-red" /> Device split
                </h2>
                <div style={{ width: "100%", height: 280 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={data.devices}
                        dataKey="count"
                        nameKey="device"
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={3}
                      >
                        {data.devices.map((entry, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #0E2A5620" }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Top pages + Recent leads */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="rounded-3xl bg-white border border-brand-navy/10 p-6">
                <h2 className="font-heading font-black text-lg text-brand-navy mb-4 flex items-center gap-2">
                  <Monitor size={18} className="text-brand-red" /> Top pages
                </h2>
                <div className="divide-y divide-brand-navy/10">
                  {data.top_pages.length === 0 && <div className="text-sm text-brand-navy/55">No page views yet.</div>}
                  {data.top_pages.map((p) => (
                    <div key={p.path} className="flex items-center justify-between py-2.5 gap-3">
                      <div className="font-mono text-xs text-brand-navy truncate">{p.path}</div>
                      <div className="text-sm font-bold text-brand-navy whitespace-nowrap">
                        {p.views} <span className="text-brand-navy/45 font-medium">views · {p.sessions} sessions</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-3xl bg-white border border-brand-navy/10 p-6">
                <h2 className="font-heading font-black text-lg text-brand-navy mb-4 flex items-center gap-2">
                  <Mail size={18} className="text-brand-red" /> Recent leads
                </h2>
                <div className="divide-y divide-brand-navy/10 max-h-[280px] overflow-auto">
                  {leads.length === 0 && <div className="text-sm text-brand-navy/55">No leads yet.</div>}
                  {leads.map((l) => (
                    <div key={l.id} className="py-3">
                      <div className="flex items-baseline justify-between gap-2">
                        <div className="font-heading font-bold text-sm text-brand-navy truncate">{l.name}</div>
                        <div className="text-[11px] text-brand-navy/55 font-medium whitespace-nowrap">{(l.created_at || "").slice(0, 16).replace("T", " ")}</div>
                      </div>
                      <div className="text-xs text-brand-navy/70 truncate">
                        <a href={`mailto:${l.email}`} className="text-brand-red font-semibold">{l.email}</a>
                        {l.phone ? <> · {l.phone}</> : null}
                      </div>
                      {l.message ? (
                        <div className="text-xs text-brand-navy/65 mt-1 line-clamp-2">{l.message}</div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Referrers */}
            <div className="rounded-3xl bg-white border border-brand-navy/10 p-6">
              <h2 className="font-heading font-black text-lg text-brand-navy mb-4">Top referrers</h2>
              {data.referrers.length === 0 ? (
                <div className="text-sm text-brand-navy/55">Mostly direct traffic so far.</div>
              ) : (
                <div className="divide-y divide-brand-navy/10">
                  {data.referrers.map((r) => (
                    <div key={r.referrer} className="flex items-center justify-between py-2 gap-3">
                      <div className="text-xs text-brand-navy/85 truncate">{r.referrer}</div>
                      <div className="text-sm font-bold text-brand-navy">{r.count}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminAnalytics;
